"use client"

import { useState, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Clock, Plus, Minus, Trash2 } from "lucide-react"
import { Switch } from "@/components/ui/switch"
import { useAvailableServices } from "@/hooks/modal/useAvailableServices"
import { useAppointmentModal } from "@/contexts/AppointmentModalContext"
import { useComputedPayment } from "@/hooks/modal/useComputedPayment";
import { addSecondsToTime } from "@/lib/utils"
import { lastHourOfferPct } from "@/lib/appointment/utils"
// Centralized cost calculation engine - single source of truth
import { getServiceUnitPrice, getServiceLineTotal } from "@/lib/appointment/costEngine"
import { getServiceRegistry } from "@/lib/services/serviceRegistrySingleton"
import type { Service } from "@/lib/types/appointment"

export function StatusSection() {
  const { t } = useTranslation()
  const { appointment, updateAppointment } = useAppointmentModal();
  const [autoDuration, setAutoDuration] = useState(true);

  // Company ID is hardcoded as in API usage; masterId from appointment.staff?.id (ensure number)

  const masterId = appointment?.staff?.id ? Number(appointment.staff.id) : undefined;

  const { data: availableServices } =
    useAvailableServices(masterId);

  const lengthByServiceId = useMemo(() => {
    if (!Array.isArray(availableServices)) return new Map<number, number>();
    return new Map<number, number>(
      availableServices
        .map((s) => [Number(s.id), typeof s.length === "number" ? s.length : undefined] as const)
        .filter(([, len]) => Number.isFinite(len)) as [number, number][]
    );
  }, [availableServices]);

  // Resolve effective duration (seconds) for a service using ServiceRegistry with master overrides
  const resolveServiceDurationSeconds = (service: Service, masterId?: number): number => {
    const registry = getServiceRegistry?.();
    const idNum = Number(service.id);
    let duration = 0;

    if (registry && Number.isFinite(idNum)) {
      const descriptor =
        registry.getServiceById?.(idNum) ||
        registry.getSnapshot?.()?.services?.[idNum];

      if (descriptor) {
        const rawCustom =
          masterId && descriptor.staffDurationSeconds
            ? descriptor.staffDurationSeconds[masterId]
            : undefined;
        const custom = Number(rawCustom);
        if (Number.isFinite(custom) && custom > 0) {
          duration = custom;
        } else {
          const def = Number(descriptor.duration_seconds);
          if (Number.isFinite(def) && def > 0) {
            duration = def;
          }
        }
      }
    }

    if (!duration && idNum && lengthByServiceId.has(idNum)) {
      duration = lengthByServiceId.get(idNum) ?? 0;
    }

    if (!duration && typeof service.length === "number" && service.length > 0) {
      duration = service.length;
    }

    return duration > 0 ? duration : 0;
  };

  const buildDurationDetails = (totalLengthSec: number, startTime: string) => {
    const newEndTime = addSecondsToTime(startTime, totalLengthSec);
    const hours = Math.floor(totalLengthSec / 3600);
    const minutes = Math.floor((totalLengthSec % 3600) / 60);
    let durationStr = t("common.labels.zero_minutes", "0 min");
    if (hours > 0 && minutes > 0) durationStr = `${hours} ${t("common.labels.hour_short", "hr")} ${minutes} ${t("common.labels.minutes_short", "min")}`;
    else if (hours > 0) durationStr = `${hours} ${t("common.labels.hour_short", "hr")}`;
    else if (minutes > 0) durationStr = `${minutes} ${t("common.labels.minutes_short", "min")}`;
    durationStr += ` (${startTime} – ${newEndTime})`;

    return {
      endTime: newEndTime,
      detailsSection: {
        startTimeString: startTime,
        endTimeString: newEndTime,
        serviceDurationString: durationStr,
        durationHours: hours,
        durationMinutes: minutes,
      },
    };
  };

  /**
   * Пересчитывает endTime на основе суммарной длины услуг.
   * Если autoDuration выключен или startTime не задан, меняем только services.
   */
  function handleRecalcEndTime(servicesAfterChange: Service[]) {
    if (!appointment) return;

    if (!autoDuration || !appointment.startTime) {
      updateAppointment({ services: servicesAfterChange });
      return;
    }

    const totalLengthSec = servicesAfterChange.reduce((sum, s) => {
      const amount = typeof s.amount === "number" && !Number.isNaN(s.amount) ? s.amount : 1;
      const duration = resolveServiceDurationSeconds(s, masterId);
      return sum + duration * amount;
    }, 0);

    if (!Number.isFinite(totalLengthSec) || totalLengthSec <= 0) {
      updateAppointment({ services: servicesAfterChange });
      return;
    }

    const durationPatch = buildDurationDetails(totalLengthSec, appointment.startTime);

    updateAppointment({ services: servicesAfterChange, ...durationPatch });
  }

  const payment = useComputedPayment();

  if (!appointment) {
    return (
      <div className="flex-1 overflow-y-auto p-6 flex items-center justify-center text-gray-500">
        {t("appointment.details.loading", "Loading appointment data...")}
      </div>
    );
  }

  // Handlers to update appointment state in context
  const handleAttendanceChange = (value: number) => {
    updateAppointment({ attendance: value });
  };

  const handleConfirmedChange = () => {
    updateAppointment({ confirmed: appointment.confirmed === 1 ? 0 : 1 });
  };

  const handleRemoveService = (id: number | string) => {
    if (!Array.isArray(appointment.services)) return;
    // An appointment must keep at least one service — saving an empty list
    // strips every yrecordservice row and orphans the record. The backend
    // rejects it too; this stops the admin reaching a save that can only fail.
    // Removing the appointment itself is Delete, not an empty service list.
    if (appointment.services.length <= 1) return;
    const updatedServices = appointment.services.filter((service) => service.id !== id);
    handleRecalcEndTime(updatedServices);
  };

  return (
    <div className="flex-1 overflow-y-auto p-6">
      {/* Status tabs */}
      <div className="flex mb-6 gap-2">
        <button
          className={`flex items-center px-3 py-1.5 text-xs rounded-md ${appointment.attendance === 0
            ? "bg-amber-50 border border-amber-200 text-amber-700"
            : "border border-gray-300 text-gray-600"
            }`}
          onClick={() => handleAttendanceChange(0)}
        >
          <Clock className="w-3.5 h-3.5 mr-1.5" />
          {t("appointment.status.waiting_client", "Waiting for client")}
        </button>
        <button
          className={`flex items-center px-3 py-1.5 text-xs rounded-md ${appointment.attendance === 1
            ? "bg-green-50 border border-green-200 text-green-700"
            : "border border-gray-300 text-gray-600"
            }`}
          onClick={() => handleAttendanceChange(1)}
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" />
          {t("appointment.status.client_arrived", "Client arrived")}
        </button>
        <button
          className={`flex items-center px-3 py-1.5 text-xs rounded-md ${appointment.attendance === -1
            ? "bg-red-50 border border-red-200 text-red-700"
            : "border border-gray-300 text-gray-600"
            }`}
          onClick={() => handleAttendanceChange(-1)}
        >
          <Minus className="w-3.5 h-3.5 mr-1.5" />
          {t("appointment.status.client_no_show", "Client did not show")}
        </button>
        <button
          className={`flex items-center px-3 py-1.5 text-xs rounded-md ${appointment.confirmed === 1 ? "bg-gray-800 text-white" : "border border-gray-300 text-gray-600"
            }`}
          onClick={handleConfirmedChange}
        >
          <span className="w-3.5 h-3.5 mr-1.5">✓</span>
          {t("appointment.status.client_confirmed", "Client confirmed")}
        </button>
      </div>

      {/* Services table */}
      <div className="mb-6">
        <div className="grid grid-cols-12 gap-2 mb-2 text-xs text-gray-500 px-1">
          <div className="col-span-6">{t("appointment.details.service_or_product", "Service or product")}</div>
          <div className="col-span-1 text-center">{t("appointment.details.quantity", "Qty")}</div>
          <div className="col-span-2 text-center">{t("appointment.details.price_currency", "Price")}</div>
          <div className="col-span-1 text-center">{t("appointment.details.discount_percent", "Discount, %")}</div>
          <div className="col-span-2 text-center">{t("appointment.details.to_pay_currency", "To pay")}</div>
        </div>

        {Array.isArray(appointment.services) && appointment.services.map((service: Service) => {
          // Use shared helpers for correct price calculation
          const unitPrice = getServiceUnitPrice(service);
          const finalPrice = getServiceLineTotal(service);

          const lhPct = lastHourOfferPct(service);
          return (
            <div key={service.id} className="relative grid grid-cols-12 gap-2 mb-3 items-center">
              {lhPct !== null && (
                <span
                  className="absolute -top-2 right-8 z-10 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold text-white shadow-sm"
                  title={t("appointment.last_hour_hint", "Booked via the last-hour offer — the badge shows the original offer discount")}
                >
                  ⚡ {t("appointment.last_hour_badge", "Last-minute")}{lhPct > 0 ? ` −${lhPct}%` : ""}
                </span>
              )}
              <div className="col-span-6">
                <div className="flex items-start">
                  <div className="mt-1 mr-2">
                    <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center">
                      <span className="text-[10px]">⚙️</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs">{service.title}</div>
                    <div className="text-xs text-gray-500">{appointment.staff?.name || ""}</div>
                  </div>
                </div>
              </div>
              <div className="col-span-1 flex justify-center items-center">
                <span className="text-center text-xs">{typeof service.amount === "number" ? service.amount : 1}</span>
              </div>
              <div className="col-span-2 text-center text-xs">{unitPrice}</div>
              <div className="col-span-1 text-center text-xs">{typeof service.discount === "number" ? service.discount : 0}</div>
              <div className="col-span-1 text-center text-xs">{finalPrice}</div>
              <div className="col-span-1 flex justify-end">
                <button
                  className="w-6 h-6 flex items-center justify-center text-gray-400 disabled:opacity-40 disabled:cursor-not-allowed"
                  onClick={() => handleRemoveService(service.id)}
                  disabled={(appointment.services?.length ?? 0) <= 1}
                  title={
                    (appointment.services?.length ?? 0) <= 1
                      ? t("appointment.modal.last_service_required", "An appointment must keep at least one service")
                      : undefined
                  }
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {/* Notes */}
      <div className="mb-6">
        <div className="text-xs mb-2">{t("appointment.details.note", "Note")}</div>
        <textarea
          className="w-full border border-gray-300 rounded-md p-2 h-24 text-xs"
          placeholder={t("appointment.details.add_comment_placeholder", "Add a comment to the appointment")}
          value={appointment.comment || ""}
          onChange={e => updateAppointment({ comment: e.target.value })}
        ></textarea>
      </div>

      {/* Auto duration toggle */}
      <div className="flex items-center justify-between mb-6">
        <div className="text-xs">{t("appointment.details.auto_duration_toggle", "Adjust duration when adding/removing services")}</div>
        <Switch
          checked={autoDuration}
          onCheckedChange={setAutoDuration}
          className="data-[state=checked]:bg-amber-500"
        />
      </div>

      {/* Visit time */}
      <div className="mb-6">
        <div className="text-xs mb-2">{t("appointment.details.visit_time", "Visit time")}</div>
        <div className="flex justify-between items-center">
          <div className="text-xs text-gray-500">{t("appointment.details.start_end", "Start — End")}</div>
          <div className="text-xs">
            <span className="font-medium">{appointment.detailsSection?.serviceDurationString ?? ""}</span>
            <span className="text-gray-500 ml-2">
              {appointment.detailsSection?.startTimeString ?? ""} – {appointment.detailsSection?.endTimeString ?? ""}
            </span>
          </div>
        </div>
      </div>

      {/* Total */}
      <div className="mb-6">
        <div className="text-xs text-gray-500 mb-1">{t("appointment.details.total_to_charge", "Total to charge")}</div>
        <div className="text-xl font-medium">{payment?.totalAmountString ?? ""}</div>
      </div>

    </div>
  )
}
